import { DecisionGraph } from "@imminently/decisively-core";
import React, { useMemo } from "react";
import type { Release } from "@packages/commons";
import { useLocalStorage } from "react-use";
import ReleaseProvider from "@imminently/decisively-ui/ReleaseProvider";

export interface ReleaseContextState {
  release: Release;
  highlightConnectedTo: string | undefined;
  setHighlightConnectedTo: (value: string | undefined) => void;
  /** Each node's (path's) distance from highlightConnectedTo along successors: Infinity when not connected. */
  highlightConnectedToDistances: {
    [node: string]: {
      distance: number,
    }
  } | undefined;
}

const ReleaseContext = React.createContext<ReleaseContextState | undefined>(undefined);

export const useRelease = () => {
  const context = React.useContext(ReleaseContext);
  if (context === undefined) {
    throw new Error("useRelease must be used within a ReleaseContext");
  }
  return context;
};

export const useMaybeRelease = () => React.useContext(ReleaseContext);

export interface ReleaseContextProviderProps {
  release: Release;
  children: React.ReactNode;
}


const ReleaseContextProvider = (props: ReleaseContextProviderProps) => {
  const { release, children } = props;

  const [highlightConnectedTo, setHighlightConnectedTo] = useLocalStorage<string | undefined>(
    `highlightConnectedTo-${release?.id}`,
    undefined,
  );

  const highlightConnectedToDistances = useMemo(() => {
    if (!highlightConnectedTo || !release?.rule_graph) {
      return undefined;
    }
    const graph = DecisionGraph.from(release.rule_graph as any);
    const from = graph.ref(highlightConnectedTo);
    if (from === undefined || !graph.isListed(from)) {
      setHighlightConnectedTo(undefined);
      return undefined;
    }

    // every node's distance along successors, unreached ones at Infinity (as dijkstra gave them)
    const reached = graph.distances(from, "successors");
    const distances: NonNullable<ReleaseContextState["highlightConnectedToDistances"]> = {};
    for (const id of graph.ids()) {
      distances[graph.deref(id)] = { distance: reached.get(id) ?? Number.POSITIVE_INFINITY };
    }
    return distances;
  }, [highlightConnectedTo, release?.rule_graph, setHighlightConnectedTo]);

  return (
    <ReleaseContext.Provider
      value={{
        release,
        setHighlightConnectedTo,
        highlightConnectedTo,
        highlightConnectedToDistances,
      }}
    >
      <ReleaseProvider release={release}>{children}</ReleaseProvider>
    </ReleaseContext.Provider>
  );
};

export default ReleaseContextProvider;